define('presentation/common/hubSpotSchedulerResizeBinding',[],() => {
    const IFRAME_SELECTOR = 'iframe';
    const IFRAME_WAIT_INTERVAL_MS = 200;
    const IFRAME_WAIT_TIMEOUT_MS = 30000;
    const MEASURED_CLASS = 'hubspot-scheduler--measured';

    return {
        /** The HubSpot embed injects its iframe asynchronously and then drives that iframe's height
         *  itself, shrinking it hard once the booking confirmation replaces the scheduler. Nothing in
         *  the embed reports that height to the host page, so it is observed off the element instead
         *  and mirrored onto the container, whose reserved min-height is released at the same time. */
        init: (element, valueAccessor) => {
            const options = valueAccessor() || {};
            const onBookingSucceeded = options.onBookingSucceeded;

            let resizeObserver = null;
            let schedulerFrame = null;
            let waitIntervalId = null;
            let pendingScrollFrame = null;
            let isDisposed = false;
            let hasReportedBooking = false;

            const applyHeight = (height) => {
                if (height > 0) {
                    element.classList.add(MEASURED_CLASS);
                    element.style.height = `${height}px`;
                }
            };

            const observeIframe = (iframe) => {
                schedulerFrame = iframe;
                /* jshint ignore:start */
                resizeObserver = new ResizeObserver((entries) => {
                    applyHeight(entries[0].target.getBoundingClientRect().height);
                });
                /* jshint ignore:end */
                resizeObserver.observe(iframe);
                applyHeight(iframe.getBoundingClientRect().height);
            };

            const waitForIframe = () => {
                const startedAt = new Date().getTime();

                waitIntervalId = setInterval(() => {
                    if (isDisposed) {
                        return;
                    }

                    const iframe = element.querySelector(IFRAME_SELECTOR);
                    if (iframe !== null) {
                        clearInterval(waitIntervalId);
                        waitIntervalId = null;
                        observeIframe(iframe);
                        return;
                    }

                    // A scheduler that never arrives leaves the reserved height in place rather than
                    // polling for the life of the page.
                    if (new Date().getTime() - startedAt > IFRAME_WAIT_TIMEOUT_MS) {
                        clearInterval(waitIntervalId);
                        waitIntervalId = null;
                    }
                }, IFRAME_WAIT_INTERVAL_MS);
            };

            // The embed posts its payload under `message` on some versions and `data` on others - its
            // own handler reads `event[event.message ? 'message' : 'data']`, so this mirrors that - and
            // some builds post it as a JSON string rather than an object.
            const readPayload = (event) => {
                const raw = event.message === undefined ? event.data : event.message;

                if (typeof raw === 'string') {
                    try {
                        return JSON.parse(raw);
                    } catch (exception) {
                        return null;
                    }
                }

                return raw === undefined ? null : raw;
            };

            const handleMessage = (event) => {
                if (schedulerFrame !== null && event.source !== schedulerFrame.contentWindow) {
                    return;
                }

                const payload = readPayload(event);
                if (payload === null || typeof payload !== 'object') {
                    return;
                }

                // Older embeds sent meetingCreated for the same thing.
                if (payload.meetingBookSucceeded !== true && payload.meetingCreated !== true) {
                    return;
                }

                if (hasReportedBooking === false) {
                    hasReportedBooking = true;

                    if (onBookingSucceeded !== undefined && onBookingSucceeded !== null) {
                        onBookingSucceeded();
                    }
                }
            };

            window.addEventListener('message', handleMessage, false);
            waitForIframe();

            ko.utils.domNodeDisposal.addDisposeCallback(element, () => {
                isDisposed = true;
                window.removeEventListener('message', handleMessage, false);

                if (pendingScrollFrame !== null) {
                    window.cancelAnimationFrame(pendingScrollFrame);
                    pendingScrollFrame = null;
                }

                if (waitIntervalId !== null) {
                    clearInterval(waitIntervalId);
                    waitIntervalId = null;
                }
                if (resizeObserver !== null) {
                    resizeObserver.disconnect();
                    resizeObserver = null;
                }
            });
        }
    };
});

